'use client';

/**
 * @fileoverview Connected-viewer-id context for the Discover surface (WEB-450).
 *
 * The single source of truth for the `userId` cache-key discriminator that
 * every discover subscriber threads into {@link buildDiscoverQueryArgs} (via
 * {@link useDiscoverEventArgs}). Centralising it here is what keeps the
 * cold-load single-fetch invariant intact once identity enters the cache key:
 * if some subscribers keyed on the viewer id and others didn't, a connected
 * cold load would open two cache entries and fire two `/events/discover`
 * requests. Reading one shared context value guarantees they all key
 * identically.
 *
 * ## Why an SSR-seeded value (and not `useAuth()` alone)
 *
 * `useAuth().user` is `null` on the first client paint — the RTK Query
 * `/auth/me` bootstrap only runs in a post-mount effect (see `AuthContextRTK`).
 * If subscribers read the viewer id straight from `useAuth()`, their first
 * render would key anonymously, read the anonymous SSR seed, then re-key once
 * `/auth/me` resolves — a visible flash of anonymous data. The Server Component
 * already resolves the viewer id (`getOptionalAuthUserId`) to build hero meta,
 * so we thread it in as `ssrViewerId`. The provider prefers the live auth
 * session id (read from `AuthContext`) once it lands and falls back to the SSR
 * value, so the context value is stable from first paint for a normal session
 * (both equal the same id ⇒ no re-key, no extra fetch).
 *
 * The SSR `/events/discover` seed itself stays **anonymous-keyed** (the SSR
 * fetch is cookieless — see `serverDiscoverFetch.ts`), so a connected viewer's
 * subscription intentionally misses that seed and fires exactly one
 * personalized fetch with cookies attached, while anonymous viewers keep
 * reading the seed with zero fetches.
 */

import * as React from 'react';

import { AuthContext } from '@/contexts/AuthContextRTK';

/**
 * Undefined = anonymous / not-yet-resolved. The default (no provider) is
 * `undefined`, so any subscriber rendered outside a `DiscoverViewerIdProvider`
 * (Storybook, isolated unit tests) keys anonymously and behaves exactly as it
 * did before WEB-450.
 */
const DiscoverViewerIdContext = React.createContext<string | undefined>(undefined);

export interface DiscoverViewerIdProviderProps {
  /**
   * Viewer id resolved server-side (`getOptionalAuthUserId`) at SSR time.
   * Seeds the first client render so subscriber args match from first paint.
   * Omitted / `undefined` for anonymous requests.
   */
  ssrViewerId?: string;
  children: React.ReactNode;
}

/**
 * Provide the connected viewer id to the discover subtree. Prefers the live
 * client session id once `/auth/me` resolves, falling back to the SSR-resolved
 * id so the value is stable across hydration for a normal session.
 */
export function DiscoverViewerIdProvider({
  ssrViewerId,
  children,
}: DiscoverViewerIdProviderProps): React.ReactElement {
  // Read the auth context directly (not `useAuth()`) so the provider never
  // throws when the discover template is rendered without an `AuthProvider`
  // ancestor (Storybook, isolated unit tests). A missing provider ⇒ `null` ⇒
  // fall back to the SSR-resolved id (or anonymous).
  const auth = React.useContext(AuthContext);
  const viewerId = auth?.user?.id ?? ssrViewerId;
  return (
    <DiscoverViewerIdContext.Provider value={viewerId}>{children}</DiscoverViewerIdContext.Provider>
  );
}

/**
 * Read the connected viewer id for the discover cache key. Returns `undefined`
 * for anonymous viewers (and when rendered without a provider).
 */
export function useDiscoverViewerId(): string | undefined {
  return React.useContext(DiscoverViewerIdContext);
}
